import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Types
import { Workspace } from 'Shared/types/Workspace.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { updateWorkspace } from 'Shared/actions/workspaces';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import { WpForm, WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WorkspaceSettingsContainer from './WorkspaceSettingsContainer';
import DeleteWorkspaceModal from 'Shared/components/DeleteWorkspaceModal/DeleteWorkspaceModal';
import ExportWorkspaceModal from 'Shared/components/ExportWorkspaceModal/ExportWorkspaceModal';
import TransferWorkspaceModal from 'Shared/components/TransferWorkspaceModal/TransferWorkspaceModal';
import WorkspaceDetailsAgendaAI from './WorkspaceDetailsAgendaAI';

// Styles
import './WorkspaceDetailsSettings.scss';

type Props = {
  workspace: Workspace;
  toggleSettingsModal: Function;
};

const WorkspaceDetailsSettings = ({ workspace, toggleSettingsModal }: Props) => {
  const { featureFlagAgendaAi } = useFlags();
  const dispatch = useDispatch();

  const [isSavingName, toggleIsSavingName] = useToggle(false);
  const [isDeleteModalOpen, toggleIsDeleteModalOpen] = useToggle(false);
  const [isExportModalOpen, toggleIsExportModalOpen] = useToggle(false);
  const [isTransferModalOpen, toggleIsTransferModalOpen] = useToggle(false);

  const { id: workspaceId, name, description, isPersonal, isOneOnOne, isGroup, isTeamSync } = workspace;

  const handleUpdateWorkspace = useCallback(values => {
    toggleIsSavingName();
    dispatch(updateWorkspace(workspaceId, { name: values.name, description: values.description })).then(() => {
      toggleIsSavingName();
    });
  }, []);

  const baseClassName = 'WorkspaceDetailsSettings';

  return (
    <>
      <div className={baseClassName}>
        {isGroup && (
          <WorkspaceSettingsContainer title="Workspace Name">
            <WpForm onSubmit={handleUpdateWorkspace}>
              <WpFormLayout>
                <WpTextField id="name" label="Workspace Name" value={name} labelHidden required max={40} />
                {!isTeamSync && <WpTextField id="description" label="Description" max={100} value={description} />}
              </WpFormLayout>
              <WpButton btnType="submit" loading={isSavingName}>
                Save
              </WpButton>
            </WpForm>
          </WorkspaceSettingsContainer>
        )}
        {featureFlagAgendaAi && (
          <WorkspaceSettingsContainer title="AgendaAI" subTitle="Get topics suggested from the tools you use everyday.">
            <WorkspaceDetailsAgendaAI toggleSettingsModal={toggleSettingsModal} />
          </WorkspaceSettingsContainer>
        )}
        {isOneOnOne && (
          <WorkspaceSettingsContainer
            title="Transfer Workspace"
            subTitle="Swap one member of the workspace out with another member within the organization."
          >
            <WpButton variant="secondary" onClick={toggleIsTransferModalOpen}>
              <WpIcon name="people-arrows" />
              Transfer
            </WpButton>
          </WorkspaceSettingsContainer>
        )}
        <WorkspaceSettingsContainer
          title="Export Activity"
          subTitle="Export recent activity to a .csv or .txt to quickly share progress reports"
        >
          <WpButton variant="secondary" onClick={toggleIsExportModalOpen}>
            <WpIcon name="file-export" />
            Export
          </WpButton>
        </WorkspaceSettingsContainer>
        {!isPersonal && !isTeamSync && (
          <WorkspaceSettingsContainer title="Delete Workspace">
            <WpButton variant="secondary" color="danger" onClick={toggleIsDeleteModalOpen}>
              <WpIcon name="trash" />
              Delete
            </WpButton>
          </WorkspaceSettingsContainer>
        )}
      </div>
      <DeleteWorkspaceModal workspaceId={workspaceId} isOpen={isDeleteModalOpen} onToggle={toggleIsDeleteModalOpen} />
      {isExportModalOpen && <ExportWorkspaceModal onToggle={toggleIsExportModalOpen} />}
      {isTransferModalOpen && <TransferWorkspaceModal onToggle={toggleIsTransferModalOpen} />}
    </>
  );
};

export default WorkspaceDetailsSettings;
